import React, { useState, useRef } from "react";
import { Modal, Button, Image, Alert } from "react-bootstrap";
import UserService from "../../api-service/UserService";
import {
  INVALID_IMAGE_FILE,
  FILE_SIZE_TOO_LARGE,
  PROFILE_UPDATED_SUCCESSFULLY,
  ERROR_WHILE_UPDATING_PROFILE_PICTURE,
  SESSION_HAS_EXPIRED,
  INCOMING_TOKEN_HAS_EXPIRED,
} from "../../configurations/MessageConstants";
import {
  MAX_PROFILE_PICTURE_SIZE,
  EMPTY,
  DANGER,
  INFO,
  SESSION_EXPIRED,
  SESSION_EXPIRED_MESSAGE,
} from "../../configurations/Constants";
import Spinner from "react-bootstrap/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { setUserDetails } from "../../configurations/redux-configurations/actions/userAction";
import StorageHelper from "../../helpers/StorageHelper";
import { StatusCodes } from "../../configurations/status-codes";
import SessionHelper from "../../helpers/SessionHelper";

function UploadPicturePopup({ show, onClose, handleAlert }) {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user.userInfo);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    setValidationError(EMPTY);
    if (file) {
      if (!file.type.startsWith("image/")) {
        setValidationError(INVALID_IMAGE_FILE);
        return;
      }
      if (file.size > MAX_PROFILE_PICTURE_SIZE) {
        setValidationError(FILE_SIZE_TOO_LARGE);
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setValidationError(EMPTY);
    setIsDragOver(false);
    onClose();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("profile_pic", selectedFile);
    try {
      const response = await UserService.updateProfileInfo(formData);
      dispatch(
        setUserDetails({
          ...userInfo,
          profile_pic: response.data.data.profile_pic,
        })
      );
      handleAlert(PROFILE_UPDATED_SUCCESSFULLY, INFO);
    } catch (error) {
      const responseData = error.response.data;
      console.error(ERROR_WHILE_UPDATING_PROFILE_PICTURE, error);
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        StorageHelper.setItem(SESSION_EXPIRED_MESSAGE, SESSION_HAS_EXPIRED);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      } else {
        handleAlert(
          error.response.status === StatusCodes.REQUEST_TOO_LONG
            ? FILE_SIZE_TOO_LARGE
            : responseData.message,
          DANGER
        );
      }
    } finally {
      setLoading(false);
      handleCancel();
    }
  };

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Profile Picture</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {validationError && (
          <Alert variant="danger" className="mb-3">
            {validationError}
          </Alert>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept=".jpg,.jpeg,.png"
          className="d-none"
        />
        <div
          className={`drag-drop-area ${isDragOver ? "drag-over" : EMPTY}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          {previewUrl ? (
            <Image
              src={previewUrl}
              roundedCircle
              height={180}
              width={180}
              className="profile-picture-preview"
            />
          ) : (
            <>
              <i
                className="fa-solid fa-camera p-2 text-muted"
                style={{ fontSize: "30px" }}
              ></i>
              <p className="text-muted p-2">Drag and drop your image here</p>
            </>
          )}
        </div>
        {!previewUrl && (
          <>
            <p className="text-muted"> --- Or ---</p>
            <Button
              variant="outline-secondary"
              onClick={handleUploadClick}
              className="mb-3"
              size="sm"
            >
              Click To Upload
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!selectedFile || loading}
        >
          {loading ? (
            <div>
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              <span>Uploading...</span>
            </div>
          ) : (
            "Upload"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default UploadPicturePopup;
